import * as React from 'react';
import { connect } from 'react-redux';
import { QueryTable, QueryTableColumn } from "../../../misc/components/queryTable/QueryTable";

import { formatDateTime, formatNull } from "../../../misc/components/Formatters/Formatters";
import {
    Query,
    Permission,
    SpirometryAnalysisSet,
    User,
    DlcoAnalysisSet,
    OverreadStatusIdEnum
} from "../../../misc/api/apiTypes";
import { Authorization } from "../../../misc/components/Authorization";
import { HamburgerMenu } from '../../../misc/components/HamburgerMenu';
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';
import { MainState } from '../../../Core/reducers';
import SiteIdFilterModal, { SiteIdHeader } from "../../Shared/FilterModals/SiteIdFilterModal";
import SubjectIdFilterModal, { SubjectIdHeader } from '../../Shared/FilterModals/SubjectIdFilterModal';
import OverreaderFilterModal, { OverreaderHeader } from "../../Shared/FilterModals/OverreaderFilterModal";
import StatusFilterModal, { StatusHeader, StatusLabelMember } from "../../Shared/FilterModals/StatusFilterModal";
import CreatedAtFilterModal, { CreatedAtFilterMember, CreatedAtHeader } from "../../Shared/FilterModals/CreatedAtFilterModal";
import StartedAtFilterModal, { StartedAtFilterMember, StartedAtHeader } from "../../Shared/FilterModals/AnalysisSets/StartedAtFilterModal";
import AnalysisSetTypeFilterModal, { AnalysisSetTypeHeader, AnalysisSetTypeFilterMember } from "../../Shared/FilterModals/AnalysisSets/AnalysisSetTypeFilterModal";
import { QueryTableState, ReceiveQueryAction, PageAction, SortAction, QueryTableActions, FilterAction } from "../../../misc/components/queryTable/Hooks/UseQueryTable";
import VisitFilterModal, { VisitFilterMember, VisitHeader } from './FilterModals/VisitFilterModal';
import SessionFilterModal, { SessionFilterMember, SessionHeader } from './FilterModals/SessionFilterModal';
import { Link } from 'react-router-dom';
import Condition from '../../../misc/components/Condition';
import { DlcoAnalysisSetTypeEnum } from '../../../misc/api/ApiDlcoAnalysisSetType';
import { useSecurityContext } from "../../../Core/SecurityContext";
import { AssignToUserInGroupModal } from '../../../misc/components/AssignToUserInGroupModal';
import SubjectTypeFilterModal, { SubjectTypeHeader } from '../../Shared/FilterModals/SubjectTypeFilterModal';
import { GetAnalysisStatus } from '../../Shared/GetAnalysisStatus';
import { useCurrentStudyConfigurationContext } from '../../../Core/CurrentStudyConfigurationContext';
import { useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';

interface DlcoAnalysisSetListTableOwnProps {
    queryTableReducer: [QueryTableState, React.Dispatch<QueryTableActions>];
}

interface DlcoAnalysisSetListTableStateProps {
    currentUser: User;
}

interface DlcoAnalysisSetListTableProps extends DlcoAnalysisSetListTableStateProps, DlcoAnalysisSetListTableOwnProps {

}

const DlcoAnalysisSetListTable = ({ queryTableReducer, currentUser }: DlcoAnalysisSetListTableProps): JSX.Element => {

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();
    const ViewOverreaderName = () => {

        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const RenderHamburgerMenu = (analysisSet: DlcoAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User) => {
        return (
            <HamburgerMenu>
                <Authorization permissionId={PermissionEnum.AccessSingleDlcoSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <li>
                        <Link to={`/Dlco/Analysis/${analysisSet.Id}/view`}>View</Link>
                    </li>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadDlcoSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >

                    <Condition test={() => analysisSet !== null
                        && analysisSet.Type.Id !== DlcoAnalysisSetTypeEnum.Site
                        && analysisSet.Analyst !== null
                        && analysisSet.Analyst?.Id === currentUser.Id
                        || analysisSet.Status.Id === OverreadStatusIdEnum.NotStarted
                    }>
                        <li>
                            <Link to={`/Dlco/Analysis/${analysisSet.Id}/overread`}>Over-read</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadDlcoSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.Type.Id === DlcoAnalysisSetTypeEnum.Overread && analysisSet.Status.Id === OverreadStatusIdEnum.Pending}>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(analysisSet.Id); setReassignToUserModalVisible(true); }}>Reassign</a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    const refreshData = () => {
        setIsLoading(true);

        //Load the relevant sessions
        legacyClient.getDlcoAnalysisSets(queryState.query).then((_populatedQuery) => {
            updateQueryData(_populatedQuery);
            setIsLoading(false);
        });
    }

    const handleReassign = async (sessionId: string, userId: string) => {
        await legacyClient.reassign(sessionId, userId);
        setReassignToUserModalVisible(false);
        refreshData();
    }

    const [queryState, dispatch] = queryTableReducer;

    const [selectedFilterModal, setSelectedFilterModal] = React.useState<string>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);

    const handleFilter = (member: string) => setSelectedFilterModal(member);

    const updateQueryData = (query: Query<DlcoAnalysisSet>) => dispatch(ReceiveQueryAction.create(query));
    const handlePage = (page: number) => dispatch(PageAction.create(page));
    const handleSort = (member: string) => dispatch(SortAction.create(member));

    // A change in the query should provoke a re-fetch of the data
    React.useEffect(() => {
        refreshData();
    }, [queryState.query.Page.Current, queryState.query.Order.Member, queryState.query.Order.Direction, queryState.query.Filters]);

    const siteNumberFilterMember = "Site.SiteNumber";
    const subjectNumberFilterMember = "Subject.SubjectNumber";
    const subjectTypeFilterMember = "Subject.SubjectTypeId";
    const analystEmailFilterMember = "Analyst.Email";

    const getModal = (): JSX.Element => {
        switch (selectedFilterModal) {
            case (siteNumberFilterMember):
                return <SiteIdFilterModal filterMember={siteNumberFilterMember} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (subjectNumberFilterMember):
                return <SubjectIdFilterModal filterMember={subjectNumberFilterMember} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (subjectTypeFilterMember):
                return <SubjectTypeFilterModal filterMember={subjectTypeFilterMember} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (VisitFilterMember):
                return <VisitFilterModal studyId={study.id} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (SessionFilterMember):
                return <SessionFilterModal studyId={study.id} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (StatusLabelMember):
                return <StatusFilterModal query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (AnalysisSetTypeFilterMember):
                return <AnalysisSetTypeFilterModal fetcher={legacyClient.getDlcoAnalysisSetTypes.bind(legacyClient)} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (CreatedAtFilterMember):
                return <CreatedAtFilterModal query={queryState.query} onClose={((from, to) => {
                    dispatch(FilterAction.create(from.Member, from.Condition, from.Values));
                    dispatch(FilterAction.create(to.Member, to.Condition, to.Values));
                    setSelectedFilterModal(null);
                })} />;
            case (StartedAtFilterMember):
                return <StartedAtFilterModal query={queryState.query} onClose={((from, to) => {
                    dispatch(FilterAction.create(from.Member, from.Condition, from.Values));
                    dispatch(FilterAction.create(to.Member, to.Condition, to.Values));
                    setSelectedFilterModal(null);
                })} />;
            case (analystEmailFilterMember):
                return <OverreaderFilterModal filterMember={analystEmailFilterMember} permissionToGetUserFor={PermissionEnum.OverreadDlcoSession} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;

            default:
                return null;
        }
    }

    return (
        <>
            <QueryTable
                query={queryState.query}
                rowUniqueIdentifier={row => row.Id}
                onPage={handlePage}
                onSort={handleSort}
                onFilter={handleFilter}
                isLoading={isLoading}
            >
                <QueryTableColumn
                    member={siteNumberFilterMember}
                    header={SiteIdHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Site.SiteNumber)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={subjectNumberFilterMember}
                    header={SubjectIdHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Subject.SubjectNumber)}
                    sortable={true}
                    filterable={true}
                /> 
                <QueryTableColumn
                    member={subjectTypeFilterMember}
                    header={SubjectTypeHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Subject.SubjectTypeName)}
                    sortable={true}
                    filterable={true}
                /> 
                <QueryTableColumn
                    member={VisitFilterMember}
                    header={VisitHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Visit.Name)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={SessionFilterMember}
                    header={SessionHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.SessionLabel)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={AnalysisSetTypeFilterMember}
                    header={AnalysisSetTypeHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Type.Name)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={CreatedAtFilterMember}
                    header={CreatedAtHeader}
                    map={(row: DlcoAnalysisSet) => formatDateTime(row.CreatedDateTime)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={StartedAtFilterMember}
                    header={StartedAtHeader}
                    map={(row: DlcoAnalysisSet) => formatDateTime(row.StartedDateTime)}
                    sortable={true}
                    filterable={true}
                />
                { ViewOverreaderName()
                    ? <QueryTableColumn
                        member={analystEmailFilterMember}
                    header={OverreaderHeader}
                    map={(row: DlcoAnalysisSet) => formatNull(row.Analyst ? row.Analyst.Email : null)}
                    sortable={true}
                        filterable={true} />
                    : null}
                <QueryTableColumn
                    member={StatusLabelMember}
                    header={StatusHeader}
                    map={(row: DlcoAnalysisSet) => GetAnalysisStatus(row)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member=""
                    header=""
                    map={(row: DlcoAnalysisSet) => RenderHamburgerMenu(row, currentUserPermissions, study.id, currentUser)}
                    sortable={false}
                    filterable={false}
                />
            </QueryTable>
            {getModal()}

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadDlcoSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />
        </>);
};

const mapStateToProps = (state: MainState, ownProps: DlcoAnalysisSetListTableOwnProps): DlcoAnalysisSetListTableProps => {
    return {
        queryTableReducer: ownProps.queryTableReducer,
        currentUser: state.security.currentUser
    };
}

export default connect<DlcoAnalysisSetListTableProps, {}, DlcoAnalysisSetListTableOwnProps>(mapStateToProps)(DlcoAnalysisSetListTable);
